<script lang="ts">
	import { maxCharactersPerLabel, maxLabels } from "../../../../core/schema/productOptionsConstants.js";
	import { inflectFit } from "./inflectFit.js";
	import { inflectLabel } from "./inflectLabel.js";
	import { inflectLabelCount } from "./inflectLabelCount.js";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import type { ProductLabel } from "../../../../core/schema/ProductLabel.js";
	import { ProductLabelStatus } from "../../../../core/schema/ProductLabelStatus.js";
	import { inflectCharacter } from "../../../../core/schema/utils/inflectCharacter.js";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg";
	import Button from "../Button.svelte";
	import plus from "../../../../core/assets/icons/btn-plus.svg?raw";
	import ProductOptionHeader from "./ProductOptionHeader.svelte";
	import { calculatePriceForProductOption } from "../../../../core/schema/utils/calculatePriceForProductOption.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";
	import { tick } from "svelte";

	export let labels: ProductLabel[];
	export let updateLabels: (newLabels: ProductLabel[]) => void;
	export let basePrice: number;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let changed: boolean | undefined;

	let newLabels = structuredClone(labels);

	function onChange(_labels: typeof labels): void {
		if (changed === undefined) {
			changed = false;
			return;
		}
		changed = true;
	}
	$: onChange(newLabels);

	function addLabel(): (typeof newLabels)[number] {
		const label: ProductLabel = { id: crypto.randomUUID(), text: "", status: ProductLabelStatus.ToDo };
		newLabels.push(label);
		newLabels = newLabels;
		return label;
	}

	function spreadLabels(trimmedValue: string): void {
		const words = trimmedValue.split(/\s+/u);

		let labelIndex = 0;
		const [label] = newLabels;
		if (label) {
			label.text = "";
		}

		do {
			let label = newLabels[labelIndex];
			const word = words.shift();
			if (!label) {
				label = addLabel();
			}
			if (!word) {
				break;
			}

			const newText = [label.text, word].filter(Boolean).join(" ");
			const newTextFits = newText.length <= maxCharactersPerLabel;
			/*
			 * Fill in the last label with the remaining text.
			 * Validations will stop the user from submitting the form with too long labels.
			 */
			const atLastLabel = labelIndex === maxLabels - 1;
			if (newTextFits || atLastLabel || label.text.length === 0) {
				label.text = newText;
			} else {
				labelIndex++;
				words.unshift(word);
			}
		} while (labelIndex < maxLabels);

		newLabels = newLabels;
	}

	function trimLabel(target: EventTarget | null, label: ProductLabel): string {
		const { value } = target as HTMLInputElement;
		label.text = value.trim();
		newLabels = newLabels;
		return label.text;
	}

	let firstLabelIsForced = true;
	let spreadTimeout: number | undefined;

	function removeLabel(idToRemove: string): void {
		clearTimeout(spreadTimeout);
		spreadTimeout = undefined;
		newLabels = newLabels.filter(({ id }) => id !== idToRemove);
	}

	function validateInput(target: EventTarget | null): void {
		const input = target as HTMLInputElement;
		const value = input.value.trim();

		const usedCharacters = value.length;
		if (usedCharacters === 0) {
			input.setCustomValidity("Nápis na cedulku je povinný.");
		} else if (usedCharacters > maxCharactersPerLabel) {
			if (newLabels.length === 1) {
				input.setCustomValidity("Nápis se (pokud možno) rozprostře do více cedulek.");
			} else {
				input.setCustomValidity(
					`Cedulka může mít maximálně ${maxCharactersPerLabel} ${inflectCharacter(maxCharactersPerLabel)}. Aktuálně využíváte ${usedCharacters} ${inflectCharacter(usedCharacters)}.`,
				);
			}
		} else {
			input.setCustomValidity("");
		}
		input.reportValidity();
	}

	const inputById: Record<ProductLabel["id"], HTMLInputElement | null> = {};

	function onLabelChange(target: EventTarget | null, label: ProductLabel): void {
		clearTimeout(spreadTimeout);
		spreadTimeout = undefined;
		const value = trimLabel(target, label);
		const index = newLabels.indexOf(label);
		if (index === 0 && newLabels.length <= 1) {
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			spreadTimeout = window.setTimeout(async () => {
				spreadLabels(value);
				if (newLabels.length > 1) {
					await tick();
					for (const label of newLabels) {
						const input = inputById[label.id];
						if (!input) {
							throw new Error(`Could not find input for label ${label.id}`);
						}
						validateInput(input);
						input.dispatchEvent(new Event("change", { bubbles: true }));
					}
				}
				spreadTimeout = undefined;
			}, 125);
		} else {
			validateInput(target);
		}
	}

	function forceFirstLabel(labels: ProductLabel[]): void {
		if (labels.length === 0 && firstLabelIsForced) {
			addLabel();
			firstLabelIsForced = false;
		}
	}

	$: forceFirstLabel(newLabels);

	$: minimalPrice = minimalPricesForOptions[ProductOption.Labels];
	$: currentPrice = calculatePriceForProductOption(basePrice, ProductOption.Labels, newLabels);
	$: labelsLeft = maxLabels - newLabels.length;
</script>

<ProductOptionHeader name="Přidat cedulku" image={cedulkydort} price={currentPrice} {minimalPrice}>
	Ručně psané jméno nebo krátký vzkaz na našich čokoládových destičkách z dortu udělá zároveň i krásné gesto.
</ProductOptionHeader>

<div class="grid w-full grid-cols-[auto,1fr] items-center gap-x-3 gap-y-2">
	{#each newLabels as label, index (label.id)}
		<span class="text-base font-semibold leading-5">{index + 1}.</span>
		<div class="flex w-full gap-2">
			<LabelledInput
				bind:input={inputById[label.id]}
				id="label-{label.id}"
				placeholder="Vložte nápis na cedulku"
				class="flex-1"
				required
				fullWidth
				clearable
				showValid
				on:clear={() => {
					removeLabel(label.id);
				}}
				alwaysEnableClear
				bind:value={label.text}
				on:input={(event) => {
					validateInput(event.target);
				}}
				on:change={(event) => {
					onLabelChange(event.target, label);
				}}
				withoutLabel
			/>
		</div>
	{/each}
</div>

<div class="flex flex-col">
	{#if labelsLeft > 0}
		<div class="col-start-2 flex flex-col gap-1 pt-2">
			<span class="text-2xs leading-4">
				Na produkt se
				{inflectFit(labelsLeft)}
				ještě
				{labelsLeft}
				{inflectLabel(labelsLeft)}.
			</span>
			<button type="button" class="flex items-start" on:click={addLabel}>
				<span class="text-2xs leading-4 underline">Přidat {inflectLabelCount[newLabels.length + 1]} cedulku</span>
			</button>
		</div>
	{/if}
	<Button
		icon={plus}
		iconClass="w-5 h-5"
		class="mt-4 self-end"
		on:click={() => {
			updateLabels(newLabels);
		}}
	>
		Uložit úpravu
	</Button>
</div>
